/**
 * Authenticated API Hook for App Bridge React 4.x
 *
 * Official Shopify Approach: Use shopify.idToken() API
 * - useAppBridge() returns the shopify global
 * - Call await shopify.idToken() to get fresh session tokens
 * - Tokens expire after 1 minute, must fetch on each request
 */

import { useCallback } from 'react';
import { useAppBridge } from '@shopify/app-bridge-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const useAuthenticatedApi = (shopDomain) => {
  console.log('🔥 useAuthenticatedApi hook called with shopDomain:', shopDomain);

  // Get the App Bridge instance from React context
  const app = useAppBridge();
  console.log('🔍 App Bridge instance from useAppBridge:', !!app);

  /**
   * Make authenticated API request using Shopify App Bridge idToken API
   * Per Shopify best practices: fetch fresh token on each request (1 min expiry)
   */
  const authenticatedRequest = useCallback(async (method, endpoint, data = null, params = {}) => {
    console.log(`🚀 Starting ${method} ${endpoint} request`);

    // Build URL
    const url = new URL(`${API_BASE_URL}/api/shopify-bridge${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    // Build basic headers
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (shopDomain) {
      headers['x-shopify-domain'] = shopDomain;
    }

    // Get fresh session token from App Bridge (official Shopify API)
    // Tokens expire after 1 minute, must fetch on each request
    try {
      const token = await app.idToken();
      headers['x-shopify-session-token'] = token;
      console.log('✅ Got fresh session token from App Bridge idToken()');
    } catch (error) {
      console.error('❌ Failed to get session token from App Bridge:', error);
      throw new Error('Authentication failed: Could not retrieve session token');
    }

    const config = {
      method,
      headers
    };

    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      config.body = JSON.stringify(data);
    }

    console.log('📋 Request details:', {
      url: url.toString(),
      method,
      headers,
      hasData: !!data
    });

    try {
      console.log('🔗 Making authenticated request with fresh session token...');

      // Use standard fetch - session token included in headers
      const response = await fetch(url.toString(), config);

      console.log('📥 Response received:', {
        status: response.status,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ API request failed:', errorData);
        // Server error envelope is either a string or { code, message }
        const err = new Error(
          errorData.error?.message || errorData.message
            || (typeof errorData.error === 'string' ? errorData.error : `HTTP ${response.status}`)
        );
        err.code = errorData.error?.code;
        err.status = response.status;
        throw err;
      }

      const responseData = await response.json();
      console.log('✅ API request successful:', { endpoint, status: response.status });
      return responseData;

    } catch (error) {
      console.error(`❌ API ${method} ${endpoint} failed:`, error);
      throw error;
    }
  }, [app, shopDomain]);

  /**
   * Make authenticated FormData request (for file uploads)
   * Per Shopify best practices: fetch fresh token on each request (1 min expiry)
   */
  const authenticatedFormDataRequest = useCallback(async (method, endpoint, formData) => {
    console.log(`🚀 Starting FormData ${method} ${endpoint} request`);

    // Build URL
    const url = new URL(`${API_BASE_URL}/api/shopify-bridge${endpoint}`);

    // Get fresh session token from App Bridge (official Shopify API)
    let token;
    try {
      token = await app.idToken();
      console.log('✅ Got fresh session token from App Bridge idToken() for FormData upload');
    } catch (error) {
      console.error('❌ Failed to get session token from App Bridge:', error);
      throw new Error('Authentication failed: Could not retrieve session token');
    }

    // Build headers for FormData (don't set Content-Type - browser sets with boundary)
    const headers = {
      'Accept': 'application/json',
      'x-shopify-session-token': token
    };

    if (shopDomain) {
      headers['x-shopify-domain'] = shopDomain;
    }

    const config = {
      method,
      headers,
      body: formData
    };

    console.log('📋 FormData Request details:', {
      url: url.toString(),
      method,
      headers,
      hasFormData: !!formData
    });

    try {
      console.log('🔗 Making authenticated FormData request with fresh session token...');

      const response = await fetch(url.toString(), config);

      console.log('📥 Response received:', {
        status: response.status,
        ok: response.ok
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('❌ API request failed:', errorData);
        // Server error envelope is either a string or { code, message }
        const err = new Error(
          errorData.error?.message || errorData.message
            || (typeof errorData.error === 'string' ? errorData.error : `HTTP ${response.status}`)
        );
        err.code = errorData.error?.code;
        err.status = response.status;
        throw err;
      }

      const responseData = await response.json();
      console.log('✅ FormData API request successful:', { endpoint, status: response.status });
      return responseData;

    } catch (error) {
      console.error(`❌ FormData API ${method} ${endpoint} failed:`, error);
      throw error;
    }
  }, [app, shopDomain]);

  return {
    authenticatedRequest,
    authenticatedFormDataRequest
  };
};